import { Component, EventEmitter, HostListener, Inject, ViewEncapsulation } from '@angular/core';
import { AbstractControl, FormArray, FormControl, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { formatBirthdayFromKennitala } from 'app/helpers/formatBirthdayFromKennitala';
import { formatKennitala, formatKennitalaRemoveLine } from 'app/helpers/formatKennitala';
import { ExposedListLogsExternalSystemsRes } from 'app/interfaces/responses/Exposed/exposedExternalSystemDetailsRes';
import { ExposedListEmployeeDivisionsRes } from 'app/interfaces/responses/Exposed/exposedListEmployeeDivisionsRes';
import { ExposedListPowerBIReportsRes } from 'app/interfaces/responses/Exposed/exposedListPowerBIReportsRes';
import { ExposedListUsersRes } from 'app/interfaces/responses/Exposed/exposedListUsersRes';
import { ExposedReadListUserDetailsRes } from 'app/interfaces/responses/Exposed/exposedReadListUserDetails';
import { ListConfigContextsRes } from 'app/interfaces/responses/List/listConfigContextsRes';
import { ListDocumentTemplatesRes } from 'app/interfaces/responses/List/listDocumentTemplatesRes';
import { ListRoleGroupsRes } from 'app/interfaces/responses/List/listRoleGroupsRes';
import { ListRolesRes } from 'app/interfaces/responses/List/listRolesRes';
import { ListTranslationContextsRes } from 'app/interfaces/responses/List/listTranslationContextsRes';
import { ConfigsService } from 'app/services/api/configs/configs.service';
import { LsrauthService } from 'app/services/api/lsrauth/lsrauth.service';
import { TranslationService } from 'app/services/api/translation/translation.service';
import { AlertsService } from 'app/services/system/alerts/alerts.service';
import { USER_ROLES } from 'common/constants/user-role.constants';
import { DialogGeneral } from '../../../DialogGeneral';

export interface DialogSettingsUsersDetailsData {
    user: ExposedListUsersRes;
    roles: ListRolesRes[];
    translationContexts: ListTranslationContextsRes[];
    roleGroups: ListRoleGroupsRes[];
    configContexts: ListConfigContextsRes[];
    availablePowerBIReports: ExposedListPowerBIReportsRes[];
    documentTemplates: ListDocumentTemplatesRes[];
    employeeDivisions: ExposedListEmployeeDivisionsRes[];
    externalSystems: ExposedListLogsExternalSystemsRes[];
}

@Component({
    selector: 'dialog-settings-users-details',
    templateUrl: 'dialog-settings-users-details.html',
    styleUrls: ['dialog-settings-users-details.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class DialogSettingsUsersDetails extends DialogGeneral {
    //#region KEYPRESSES

    // ------------------- Leyfir notanda að vista á ctrl+s -----------------------
    onKeyDown($event): void {
        // Detect platform
        if (navigator.platform.match('Mac')) {
            this.handleMacKeyEvents($event);
        } else {
            this.handleWindowsKeyEvents($event);
        }
    }

    handleMacKeyEvents($event) {
        // MetaKey documentation
        // https://developer.mozilla.org/en-US/docs/Web/API/KeyboardEvent/metaKey
        let charCode = String.fromCharCode($event.which).toLowerCase();
        if ($event.metaKey && charCode === 's') {
            // Action on Cmd + S
            $event.preventDefault();
            this.save();
        }
    }

    handleWindowsKeyEvents($event) {
        let charCode = String.fromCharCode($event.which).toLowerCase();
        if ($event.ctrlKey && charCode === 's') {
            // Action on Ctrl + S
            $event.preventDefault();
            this.save();
        }
    }

    //#endregion

    birthday: string = '';
    form: UntypedFormGroup;
    documentDivisionTableSource: MatTableDataSource<AbstractControl<any, any>>;
    documentDivisionsColumns: string[] = ['divisionName', 'canListDocuments', 'canReadDocuments'];
    formEnabled: boolean = true;
    onConfirm = new EventEmitter();
    rolesGroupMap = {};
    userRoles = USER_ROLES;
    user: ExposedReadListUserDetailsRes;
    loading: boolean;

    /**
     * Constructor
     */
    constructor(
        public matDialogRef: MatDialogRef<DialogSettingsUsersDetails>,
        private _formBuilder: UntypedFormBuilder,
        public dialogRef: MatDialogRef<DialogSettingsUsersDetails>,
        public dialog: MatDialog,
        private alertsService: AlertsService,
        private translationService: TranslationService,
        public lsrauthService: LsrauthService,
        public configsService: ConfigsService,
        @Inject(MAT_DIALOG_DATA) public data: DialogSettingsUsersDetailsData
    ) {
        super(dialogRef);

        for (let group of this.data.roleGroups) {
            this.rolesGroupMap[group.roleGroupId] = this.data.roles.filter((role) => role.roleGroupId == group.roleGroupId);
        }

        this.initForm();
    }

    initForm() {
        this.user = {
            name: this.data.user.displayName,
            ...this.data.user,
        } as ExposedReadListUserDetailsRes;
        if (this.user.kennitala) {
            this.birthday = formatBirthdayFromKennitala(this.user.kennitala);
        }
        this.form = this._formBuilder.group({
            configContexts: new FormControl([]),
            translationContexts: new FormControl([]),
            selectedReports: new FormControl([]),
            documentTemplateIds: new FormControl([]),
            documentDivisions: new FormArray([]),
            externalEmployee: new FormControl(this.data.user.isExternalEmployee),
            logExternalSystems: new FormControl([]),
        });
        for (const role of this.data.roles) {
            const initial = role.roleName === USER_ROLES.EMPLOYEE_LSR && this.data.user.isEmployee;
            this.form.addControl(
                role.roleName,
                new UntypedFormControl({
                    value: initial,
                    disabled: !role.editable,
                })
            );
        }

        // If the selected user has a userId, detail data is fetched from the database otherwise we have all available information in the provided list user
        if (this.data.user.userId) {
            this.loading = true;
            this.lsrauthService.getListUserDetails(this.data.user.userId).subscribe((res) => {
                this.user = res;
                if (res.kennitala) {
                    this.birthday = formatBirthdayFromKennitala(res.kennitala);
                }

                this.form = this._formBuilder.group({
                    configContexts: new FormControl(res.configContexts.map((x) => x.contextName)),
                    translationContexts: new FormControl(res.translationContexts.map((x) => x.contextName)),
                    selectedReports: new FormControl(res.reports.map((x) => x.reportKey)),
                    documentTemplateIds: new FormControl(res.documentTemplates.map((x) => x.documentTemplateId)),
                    externalEmployee: new FormControl(this.data.user.isExternalEmployee),
                    documentDivisions: new FormArray([]),
                    logExternalSystems: new FormControl(res.logsExternalSystems.map((x) => x.systemName)),
                });
                var divisionArray = this.form.get('documentDivisions') as FormArray;
                this.data.employeeDivisions.forEach((ed) => {
                    var userDivision = res.documentDivisions.find((x) => x.divisionId == ed.id);
                    divisionArray.push(
                        this._formBuilder.group({
                            divisionId: [ed.id],
                            name: [ed.name],
                            canListDocuments: [userDivision?.canListDocuments ?? false],
                            canReadDocuments: [userDivision?.canReadDocuments ?? false],
                        })
                    );
                });
                this.documentDivisionTableSource = new MatTableDataSource(divisionArray.controls);

                for (const role of this.data.roles) {
                    const hasRole = res.roles.some((x) => x.roleName == role.roleName);
                    this.form.addControl(
                        role.roleName,
                        new UntypedFormControl({
                            value: hasRole,
                            disabled: !role.editable,
                        })
                    );
                }

                this.loading = false;
            });
        }

        this.preventAccidentalClose();
    }

    formatKennitala(kt) {
        if (kt) {
            return formatKennitala(kt);
        } else {
            return '';
        }
    }

    preventAccidentalClose() {
        // Passa að hindra exit ef breytt
        this.dialogRef.disableClose = true;
        this.dialogRef.backdropClick().subscribe((_) => {
            if (this.form.dirty) {
                let cn = confirm(this.ovistadarBreytingar);
                if (cn) {
                    this.close();
                }
            } else {
                this.close();
            }
        });
    }

    revertAllChanges() {
        if (confirm(this.translationService.translate('info_confirm_revert_changes'))) {
            this.form.markAsPristine();
            this.initForm();
        }
    }

    save() {
        if (!this.user.kennitala || !this.user.userId) {
            this.alertsService.changealertHeader(this.translationService.translate('info_error'));
            this.alertsService.changeAlertErrorMessage(this.translationService.translate('error_user_ssn_not_registered_database'));
            this.alertsService.changeAlertErrorShow(true);
            return;
        }

        this.formEnabled = false;
        let roles: string[] = [];

        for (let role of this.data.roles) {
            if (this.form.get(role.roleName).value) {
                roles.push(role.roleName);
            }
        }

        this.lsrauthService
            .updateUser({
                socialSecurityNumber: this.user.kennitala,
                roles: roles,
                configContexts: roles.includes(USER_ROLES.EDIT_CONFIGS_ROLE) ? this.form.get('configContexts').value : [],
                translationContexts: roles.includes(USER_ROLES.EDIT_TRANSLATIONS_ROLE) ? this.form.get('translationContexts').value : [],
                reports: roles.includes(USER_ROLES.REPORTS) ? this.form.get('selectedReports').value : [],
                documentTemplateIds: roles.includes(USER_ROLES.DOCUMENT_TEMPLATES) ? this.form.get('documentTemplateIds').value : [],
                externalEmployee: this.form.get('externalEmployee').value,
                documentDivisions: roles.includes(USER_ROLES.DOCUMENT_ACCESS_FILES_WITH_DIVISION)
                    ? this.form.get('documentDivisions').value.filter((x) => x.canListDocuments || x.canReadDocuments)
                    : [],
                logsExternalSystems: roles.includes(USER_ROLES.EXTERNAL_SYSTEM_LOGS) ? this.form.get('logExternalSystems').value : [],
            })
            .subscribe((success) => {
                this.form.markAsPristine();
                this.formEnabled = true;
                if (success) {
                    this.onConfirm.emit(true);
                    this.close();
                }
            });
    }

    registerUser() {
        this.loading = true;
        this.lsrauthService
            .createUser({
                socialSecurityNumber: formatKennitalaRemoveLine(this.user.kennitala),
                isExternalEmployee: this.form.get('externalEmployee').value,
            })
            .subscribe((userId) => {
                this.loading = false;
                if (userId) {
                    this.data.user.userId = userId;
                    this.onConfirm.emit(true);
                    this.initForm();
                }
            });
    }

    // ------------------------------------------------------------------------------
    ovistadarBreytingar: string = this.translationService.translate('info_leave_form_dialog_message');

    @HostListener('window:keyup.esc') onKeyUp() {
        if (this.form.dirty) {
            let cn = confirm(this.ovistadarBreytingar);
            if (cn) {
                this.close();
            }
        } else {
            this.close();
        }
    }
    @HostListener('window:beforeunload', ['$event']) unloadHandler(event: Event) {
        if (this.form.dirty) {
            event.returnValue = false;
        } else {
            this.close();
        }
    }

    // ------------------------------------------------------------------------------
    // Passar að prompta alert message ef breytingar hafa átt sér stað sem hafa ekki verið vistaðar
    dialogCloseMessage() {
        if (this.form.dirty) {
            let cn = confirm(this.ovistadarBreytingar);
            if (cn) {
                this.close();
            }
        } else {
            this.close();
        }
    }
}
